<template>
  <div class="relative flex w-full flex-col items-center">
    <div class="flex w-full border border-gray-300 bg-white p-1">
      <div class="flex flex-auto flex-wrap">
        <div
          v-for="selectedType in selectedTypes"
          :key="selectedType.id"
          class="m-1 flex items-center justify-center gap-1 rounded-full border border-emerald-300 bg-emerald-100 px-2 py-1 text-zuno-turquoise"
        >
          <span
            class="selected-type-item max-w-full flex-initial text-xs font-normal leading-none"
          >{{ selectedType.name }}</span>
          <div
            class="deselect-type-item flex flex-auto cursor-pointer flex-row-reverse"
            @click="deselectType(selectedType.id)"
          >
            <TimesSmall custom-style="h-3.5 w-3.5" />
          </div>
        </div>
        <div class="flex-1">
          <input
            :placeholder="inputPlaceholder"
            readonly
            class="h-full w-full appearance-none bg-transparent p-1 px-2 text-gray-800 outline-none placeholder:text-sm placeholder:font-light placeholder:italic"
            @focus="toggleSelector()"
          >
        </div>
      </div>
      <div
        class="flex w-8 items-center border-l border-gray-200 py-1 pl-2 pr-1 text-gray-300"
      >
        <button
          v-if="selectorOpened"
          id="hide-selector"
          class="flex w-6 cursor-pointer justify-center text-gray-600 outline-none focus:outline-none"
          @click.prevent="hideSelector()"
        >
          <ChevronUpSmall />
        </button>
        <button
          v-else
          id="show-selector"
          class="h-6 w-6 cursor-pointer text-gray-600 outline-none focus:outline-none"
          @click.prevent="showSelector()"
        >
          <ChevronDownSmall />
        </button>
      </div>
    </div>
    <div
      v-if="selectorOpened"
      class="absolute left-0 top-full z-40 max-h-[300px] w-full overflow-y-auto bg-white font-light shadow"
    >
      <!-- Scopes -->
      <div
        v-for="({ categories, id, name, opened }, index) in filteredScopes"
        :key="id"
        class="w-full"
      >
        <div
          class="w-full cursor-pointer border-b bg-zuno-turquoise text-slate-50"
          :class="{
            'rounded-b': index === filteredScopes.length - 1 && !opened,
          }"
          @click="toggleScope(id)"
        >
          <div class="flex w-full items-center border-transparent p-2">
            <div class="flex w-full items-center justify-between">
              <div class="scope-item mx-2 leading-6">
                {{ name }}
              </div>
              <ChevronDoubleUpSmall
                v-if="opened"
                custom-style="h-4 w-4"
              />
              <ChevronDoubleDownSmall
                v-else
                custom-style="h-4 w-4"
              />
            </div>
          </div>
        </div>
        <div
          v-if="opened"
          class="flex w-full flex-col"
        >
          <div
            v-for="(category, categoryIndex) in categories"
            :key="category.id"
            class="w-full"
          >
            <div
              class="w-full cursor-pointer border-b bg-zuno-blue text-slate-50"
              @click="toggleCategory(id, categoryIndex)"
            >
              <div class="flex w-full items-center border-transparent p-2">
                <div class="flex w-full items-center justify-between">
                  <div class="category-item mx-2 text-sm leading-6">
                    {{ category.name }}
                  </div>
                  <ChevronDoubleUpSmall
                    v-if="category.opened"
                    custom-style="h-3 w-3"
                  />
                  <ChevronDoubleDownSmall
                    v-else
                    custom-style="h-3 w-3"
                  />
                </div>
              </div>
            </div>
            <div v-if="category.opened">
              <div
                v-for="(type, typeIndex) in category.activities"
                :key="type.id"
                class="w-full cursor-pointer bg-zuno-bone text-zuno-black"
                :class="{
                  'rounded-b':
                    typeIndex === category.activities.length - 1
                    && category.opened,
                }"
                @click.prevent="selectType(type)"
              >
                <div class="flex w-full items-center p-2">
                  <CheckSmall v-if="typeIsSelected(type.id)" />
                  <div class="flex w-full items-center">
                    <div
                      class="type-item mx-2 pl-8 text-sm font-normal leading-6"
                    >
                      {{ type.name }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Scopes -->
    </div>
  </div>
</template>

<script>
export default {
  name: 'TypeMultiSelector',
  props: {
    scopesWithTypes: {
      type: Array,
      required: true,
    },
    typesToEdit: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['typesUpdated'],

  data() {
    return {
      selectorOpened: false,
      selectedTypes: this.typesToEdit,
      filteredScopes: [],
    }
  },
  computed: {
    inputPlaceholder() {
      return this.selectedTypes.length === 0 ? 'Add source types...' : ''
    },
  },
  watch: {
    scopesWithTypes(newScopes) {
      this.filteredScopes = newScopes.map((scope) => {
        scope.opened = false
        scope.categories.sort(this.sortByName()).forEach((category) => {
          category.opened = false
          category.activities.sort(this.sortByName())
        })
        return scope
      })
    },
    typesToEdit(newTypes) {
      this.selectedTypes = newTypes
    },
  },
  mounted() {
    this.passNewTypes()
  },
  methods: {
    sortByName() {
      return (first, second) => first.name.localeCompare(second.name)
    },
    hideSelector() {
      this.selectorOpened = false
    },
    showSelector() {
      this.selectorOpened = true
    },
    toggleSelector() {
      this.selectorOpened = !this.selectorOpened
    },
    selectType(type) {
      if (!this.typeIsSelected(type.id)) {
        this.selectedTypes.push(type)
        this.passNewTypes()
      }
    },
    deselectType(id) {
      if (this.typeIsSelected(id)) {
        this.selectedTypes = this.selectedTypes.filter(
          selectedType => selectedType.id !== id,
        )
        this.passNewTypes()
      }
    },
    typeIsSelected(id) {
      return this.selectedTypes.some(selectedType => selectedType.id === id)
    },
    passNewTypes() {
      this.$emit(
        'typesUpdated',
        [...this.selectedTypes].map(newType => newType.id),
      )
    },
    toggleScope(id) {
      const scopeIndex = this.filteredScopes.findIndex(
        scope => scope.id === id,
      )
      // Close this scope's categories if the scope is opened
      if (this.filteredScopes[scopeIndex].opened) {
        this.filteredScopes = this.filteredScopes.map((scope) => {
          if (scope.id === id) {
            scope.categories.forEach((category) => {
              category.opened = false
            })
          }
          return scope
        })
      }
      // Toggle the scope itself
      this.filteredScopes = this.filteredScopes.map((scope) => {
        if (scope.id === id) {
          scope.opened = !scope.opened
        }
        return scope
      })
    },
    toggleCategory(scopeId, categoryIndex) {
      this.filteredScopes = this.filteredScopes.map((scope) => {
        if (scope.id === scopeId) {
          scope.categories[categoryIndex].opened
            = !scope.categories[categoryIndex].opened
        }
        return scope
      })
    },
  },
}
</script>
